export const OPERATIONAL_DRIVER_SCHEMA_VERSION = 'operational_driver_fact_v1' as const;
export const GOVERNED_DRIVER_FACT_ALL_MEMBER_KEY = '__all__' as const;
export const OPERATIONAL_DRIVER_A3_FIXTURE_ID =
  'operational-driver-a3-monthly-tl-ltl-fixture-v1' as const;
export const OPERATIONAL_DRIVER_A3_PROMOTION_STAGE = 'a3_fixture_promotion' as const;
export const OPERATIONAL_DRIVER_A3_FIXTURE_SOURCE_SYSTEM_REF =
  'fixture:operational-driver-a3-monthly-tl-ltl-v1' as const;

export type OperationalDriverSchemaVersion = typeof OPERATIONAL_DRIVER_SCHEMA_VERSION;
export type OperationalDriverFixturePromotionStage =
  typeof OPERATIONAL_DRIVER_A3_PROMOTION_STAGE;

export type OperationalDriverFamily =
  | 'volume'
  | 'rate'
  | 'cost'
  | 'productivity_denominator';

export type OperationalDriverMode =
  | 'truckload'
  | 'ltl'
  | 'intermodal'
  | 'ocean'
  | 'air'
  | 'managed_transportation'
  | 'warehousing'
  | 'customs'
  | 'sourcing'
  | 'other';

export type OperationalDriverPeriodType =
  | 'week'
  | 'month'
  | 'quarter'
  | 'fiscal_year';

export type OperationalDriverTimeGrain =
  | 'weekly_operational'
  | 'monthly_operational'
  | 'quarterly_operational'
  | 'annual_finance_alignment';

export type OperationalDriverDisclosureBasis =
  | 'public_filing_reported'
  | 'public_filing_directional_only'
  | 'client_operational_feed_required';

export type OperationalDriverSourceClass =
  | 'loaded_fixture'
  | 'client_operational_feed'
  | 'public_filing'
  | 'manual_admin';

export type OperationalDriverFinanceRelationship =
  | 'driver_x_rate'
  | 'rate_only'
  | 'cost_per_driver'
  | 'productivity_denominator';

export type OperationalDriverAsOfAlignmentRule =
  | 'same_period_as_of'
  | 'monthly_operational_rollup_to_annual_finance'
  | 'weekly_operational_to_monthly_run_rate';

export type GovernedDriverFinanceAsOfAlignment = Readonly<{
  rule: OperationalDriverAsOfAlignmentRule;
  operationalPeriodType: OperationalDriverPeriodType;
  financePeriodType: OperationalDriverPeriodType;
  operationalAsOf: string;
  financeAsOf: string;
}>;

export type OperationalDriverGrain = Readonly<{
  timeGrain: OperationalDriverTimeGrain;
  dimensions: readonly string[];
}>;

export type GovernedDriverFinanceBinding = Readonly<{
  financeFactDedupKey: string;
  financeMetricKey: string;
  relationship: OperationalDriverFinanceRelationship;
  rateDriverKey?: string | null;
  formulaId: string;
  reconciliationRule: string;
  asOfAlignment: GovernedDriverFinanceAsOfAlignment;
}>;

export type GovernedDriverFact = Readonly<{
  schemaVersion: OperationalDriverSchemaVersion;
  tenantId: string;
  driverKey: string;
  driverFamily: OperationalDriverFamily;
  modeKey: OperationalDriverMode;
  businessLineKey?: string | null;
  laneKey?: string | null;
  customerKey?: string | null;
  siteKey?: string | null;
  periodType: OperationalDriverPeriodType;
  periodKey: string;
  valueNumeric: string;
  unit: string;
  sourceClass: OperationalDriverSourceClass;
  sourceSystemRef: string;
  evidenceRefId: string;
  artifactId: string;
  ingestJobId: string;
  asOf: string;
  approvedAt?: string | null;
  disclosureBasis: OperationalDriverDisclosureBasis;
  grain: OperationalDriverGrain;
  financeBindings: readonly GovernedDriverFinanceBinding[];
}>;

export type GovernedDriverFactKeyInput = Pick<
  GovernedDriverFact,
  | 'tenantId'
  | 'driverKey'
  | 'modeKey'
  | 'businessLineKey'
  | 'laneKey'
  | 'customerKey'
  | 'siteKey'
  | 'periodType'
  | 'periodKey'
  | 'asOf'
>;

export type GovernedDriverFactDedupResult = Readonly<{ inserted: boolean; key: string }>;

export type GovernedDriverFinanceBridge = Readonly<{
  driverFactDedupKey: string;
  financeFactDedupKey: string;
  financeMetricKey: string;
  relationship: OperationalDriverFinanceRelationship;
  formulaId: string;
  reconciliationRule: string;
  asOfAlignment: GovernedDriverFinanceAsOfAlignment;
}>;

export type OperationalDriverFinanceFact = Readonly<{
  financeFactDedupKey: string;
  tenantId: string;
  financeMetricKey: string;
  periodType: OperationalDriverPeriodType;
  periodKey: string;
  valueNumeric: string;
  unit: string;
  sourceClass: string;
  evidenceRefId: string;
  artifactId: string;
  asOf: string;
}>;

export type OperationalDriverFixtureLoadInput = Readonly<{
  sourceFixtureId?: string;
  promotionStage?: OperationalDriverFixturePromotionStage;
  facts: readonly GovernedDriverFact[];
  financeFactKeys: readonly string[];
  financeFacts?: readonly OperationalDriverFinanceFact[];
  financeBridges: readonly GovernedDriverFinanceBridge[];
}>;

export type OperationalDriverFixtureLoadErrorCode =
  | 'duplicate_driver_fact'
  | 'invalid_driver_fact'
  | 'invalid_operational_driver_grain'
  | 'unsupported_driver_family_fixture'
  | 'missing_finance_fact_reference'
  | 'missing_reciprocal_finance_bridge'
  | 'mismatched_reciprocal_finance_bridge'
  | 'missing_as_of_alignment'
  | 'invalid_as_of_alignment'
  | 'invalid_numeric_value'
  | 'cross_grain_join_refused'
  | 'stale_operational_as_of'
  | 'missing_rate_driver_fact'
  | 'orphan_finance_bridge'
  | 'driver_requires_client_operational_feed';

export type OperationalDriverFixtureLoadError = Readonly<{
  code: OperationalDriverFixtureLoadErrorCode;
  message: string;
  driverFactDedupKey?: string;
  financeFactDedupKey?: string;
  evidenceRefId?: string;
}>;

export type OperationalDriverFixtureParentRef = Readonly<{
  refType: 'fixture_driver_fact';
  sourceFixtureId: string;
  driverFactDedupKey: string;
  evidenceRefId: string;
  artifactId: string;
  ingestJobId: string;
  asOf: string;
}>;

export type OperationalDriverPromotedFactCandidate = Readonly<{
  id: string;
  fact: GovernedDriverFact;
  promotion: Readonly<{
    pattern: OperationalDriverFixturePromotionStage;
    parent: OperationalDriverFixtureParentRef;
  }>;
}>;

export type OperationalDriverReconciliationProbe = Readonly<{
  severity: 'info';
  code: 'driver_finance_reconciliation_info';
  financeFactDedupKey: string;
  financeMetricKey: string;
  formulaId: string;
  relationship: OperationalDriverFinanceRelationship;
  driverFactDedupKey: string;
  rateDriverFactDedupKey: string;
  driverValueNumeric: number;
  rateValueNumeric: number;
  computedFinanceValueNumeric: number;
  financeValueNumeric: number;
  residualNumeric: number;
  unit: string;
  message: string;
}>;

export type OperationalDriverFixtureLoadPlan = Readonly<{
  ok: boolean;
  sourceFixtureId: string | null;
  promotionStage: OperationalDriverFixturePromotionStage | null;
  facts: readonly GovernedDriverFact[];
  financeBridges: readonly GovernedDriverFinanceBridge[];
  promotedFacts: readonly OperationalDriverPromotedFactCandidate[];
  reconciliationProbes: readonly OperationalDriverReconciliationProbe[];
  errors: readonly OperationalDriverFixtureLoadError[];
}>;

const CLIENT_FEED_ONLY_DRIVER_KEYS = new Set([
  'load_count',
  'shipment_count',
  'tonnage',
  'revenue_per_load',
  'cost_per_load',
  'loads_per_operating_fte',
  'shipments_per_operating_fte',
]);

const A3_FIXTURE_DRIVER_KEYS = new Set([
  'load_count',
  'shipment_count',
  'revenue_per_load',
  'cost_per_load',
]);

const A3_FIXTURE_MODE_KEYS = new Set<OperationalDriverMode>(['truckload', 'ltl']);

const A3_FIXTURE_TENANT_ID = 'chrw-uat';
const A3_FIXTURE_PERIOD_KEY = '2025-12';
const A3_FIXTURE_OPERATIONAL_AS_OF = '2026-01-05T00:00:00.000Z';
const A3_FIXTURE_FINANCE_AS_OF = '2026-02-15T00:00:00.000Z';
const A3_FIXTURE_ARTIFACT_ID = 'fixture:artifact:operational-driver-a3:2025-12';
const A3_FIXTURE_INGEST_JOB_ID = 'fixture:ingest:operational-driver-a3:2025-12';

export function buildGovernedDriverFactDedupKey(input: GovernedDriverFactKeyInput): string {
  return [
    input.tenantId,
    input.driverKey,
    input.modeKey,
    dimensionKey(input.businessLineKey),
    dimensionKey(input.laneKey),
    dimensionKey(input.customerKey),
    dimensionKey(input.siteKey),
    input.periodType,
    input.periodKey,
    input.asOf,
  ].join('|');
}

export function buildOperationalDriverA3MonthlyFixtureInput(): OperationalDriverFixtureLoadInput {
  const grossRevenueFinanceFact = buildA3FinanceFact({
    financeMetricKey: 'gross_revenue_usd',
    valueNumeric: '300000',
    evidenceRefId: 'fixture:evidence:finance:a3:gross-revenue:fy2025',
  });
  const purchasedTransportationFinanceFact = buildA3FinanceFact({
    financeMetricKey: 'purchased_transportation_usd',
    valueNumeric: '240000',
    evidenceRefId: 'fixture:evidence:finance:a3:purchased-transportation:fy2025',
  });
  const revenueAlignment = buildA3AsOfAlignment();
  const costAlignment = buildA3AsOfAlignment();
  const loadCountFact = buildA3DriverFact({
    driverKey: 'load_count',
    driverFamily: 'volume',
    modeKey: 'truckload',
    valueNumeric: '120',
    unit: 'loads',
    evidenceRefId: 'fixture:evidence:operational-driver-a3:truckload-load-count',
    financeBindings: [
      {
        financeFactDedupKey: grossRevenueFinanceFact.financeFactDedupKey,
        financeMetricKey: grossRevenueFinanceFact.financeMetricKey,
        relationship: 'driver_x_rate',
        rateDriverKey: 'revenue_per_load',
        formulaId: 'operational_revenue_per_load_v1',
        reconciliationRule:
          'Fixture-labeled monthly truckload loads multiplied by revenue per load reconcile to the fixture finance revenue fact.',
        asOfAlignment: revenueAlignment,
      },
      {
        financeFactDedupKey: purchasedTransportationFinanceFact.financeFactDedupKey,
        financeMetricKey: purchasedTransportationFinanceFact.financeMetricKey,
        relationship: 'cost_per_driver',
        rateDriverKey: 'cost_per_load',
        formulaId: 'operational_cost_per_load_v1',
        reconciliationRule:
          'Fixture-labeled monthly truckload loads multiplied by cost per load reconcile to the fixture finance purchased transportation fact.',
        asOfAlignment: costAlignment,
      },
    ],
  });
  const revenuePerLoadFact = buildA3DriverFact({
    driverKey: 'revenue_per_load',
    driverFamily: 'rate',
    modeKey: 'truckload',
    valueNumeric: '2500',
    unit: 'usd_per_load',
    evidenceRefId: 'fixture:evidence:operational-driver-a3:truckload-revenue-per-load',
    financeBindings: [],
  });
  const costPerLoadFact = buildA3DriverFact({
    driverKey: 'cost_per_load',
    driverFamily: 'cost',
    modeKey: 'truckload',
    valueNumeric: '2000',
    unit: 'usd_per_load',
    evidenceRefId: 'fixture:evidence:operational-driver-a3:truckload-cost-per-load',
    financeBindings: [],
  });
  const shipmentCountFact = buildA3DriverFact({
    driverKey: 'shipment_count',
    driverFamily: 'volume',
    modeKey: 'ltl',
    valueNumeric: '480',
    unit: 'shipments',
    evidenceRefId: 'fixture:evidence:operational-driver-a3:ltl-shipment-count',
    financeBindings: [],
  });
  const loadCountFactDedupKey = buildGovernedDriverFactDedupKey(loadCountFact);

  return Object.freeze({
    sourceFixtureId: OPERATIONAL_DRIVER_A3_FIXTURE_ID,
    promotionStage: OPERATIONAL_DRIVER_A3_PROMOTION_STAGE,
    facts: Object.freeze([
      loadCountFact,
      revenuePerLoadFact,
      costPerLoadFact,
      shipmentCountFact,
    ]),
    financeFactKeys: Object.freeze([
      grossRevenueFinanceFact.financeFactDedupKey,
      purchasedTransportationFinanceFact.financeFactDedupKey,
    ]),
    financeFacts: Object.freeze([grossRevenueFinanceFact, purchasedTransportationFinanceFact]),
    financeBridges: Object.freeze([
      {
        driverFactDedupKey: loadCountFactDedupKey,
        financeFactDedupKey: grossRevenueFinanceFact.financeFactDedupKey,
        financeMetricKey: grossRevenueFinanceFact.financeMetricKey,
        relationship: 'driver_x_rate' as const,
        formulaId: 'operational_revenue_per_load_v1',
        reconciliationRule:
          'Fixture-labeled monthly truckload loads multiplied by revenue per load reconcile to the fixture finance revenue fact.',
        asOfAlignment: revenueAlignment,
      },
      {
        driverFactDedupKey: loadCountFactDedupKey,
        financeFactDedupKey: purchasedTransportationFinanceFact.financeFactDedupKey,
        financeMetricKey: purchasedTransportationFinanceFact.financeMetricKey,
        relationship: 'cost_per_driver' as const,
        formulaId: 'operational_cost_per_load_v1',
        reconciliationRule:
          'Fixture-labeled monthly truckload loads multiplied by cost per load reconcile to the fixture finance purchased transportation fact.',
        asOfAlignment: costAlignment,
      },
    ] satisfies readonly GovernedDriverFinanceBridge[]),
  });
}

export function createGovernedDriverFactDeduper() {
  const keys = new Set<string>();

  return {
    upsert(input: GovernedDriverFactKeyInput): GovernedDriverFactDedupResult {
      const key = buildGovernedDriverFactDedupKey(input);
      const inserted = !keys.has(key);
      keys.add(key);
      return { inserted, key };
    },

    get size(): number {
      return keys.size;
    },
  };
}

export function buildOperationalDriverFixtureLoadPlan(
  input: OperationalDriverFixtureLoadInput,
): OperationalDriverFixtureLoadPlan {
  const errors: OperationalDriverFixtureLoadError[] = [];
  const deduper = createGovernedDriverFactDeduper();
  const financeFacts = input.financeFacts ?? [];
  const financeFactKeys = new Set([
    ...input.financeFactKeys,
    ...financeFacts.map((fact) => fact.financeFactDedupKey),
  ]);
  const financeFactByKey = new Map(
    financeFacts.map((fact) => [fact.financeFactDedupKey, fact] as const),
  );
  const driverFactKeys = new Set<string>();
  const financeBridgeByKey = new Map<string, GovernedDriverFinanceBridge>();
  const factByMatchKey = new Map<string, GovernedDriverFact>();

  for (const bridge of input.financeBridges) {
    financeBridgeByKey.set(
      bridgeKey(bridge.driverFactDedupKey, bridge.financeFactDedupKey),
      bridge,
    );
  }

  for (const financeFact of financeFacts) {
    if (!isFiniteDecimalString(financeFact.valueNumeric)) {
      errors.push({
        code: 'invalid_numeric_value',
        message: 'Finance fact valueNumeric must be a finite decimal string.',
        financeFactDedupKey: financeFact.financeFactDedupKey,
        evidenceRefId: financeFact.evidenceRefId,
      });
    }
    if (!isValidTimestamp(financeFact.asOf)) {
      errors.push({
        code: 'invalid_as_of_alignment',
        message: 'Finance fact asOf must be a valid timestamp.',
        financeFactDedupKey: financeFact.financeFactDedupKey,
        evidenceRefId: financeFact.evidenceRefId,
      });
    }
    if (!isValidPeriodKey(financeFact.periodType, financeFact.periodKey)) {
      errors.push({
        code: 'invalid_operational_driver_grain',
        message: 'Finance fact period key is invalid for its declared period type.',
        financeFactDedupKey: financeFact.financeFactDedupKey,
        evidenceRefId: financeFact.evidenceRefId,
      });
    }
  }

  for (const fact of input.facts) {
    factByMatchKey.set(driverFactMatchKey(fact, fact.driverKey), fact);
  }

  for (const fact of input.facts) {
    const dedup = deduper.upsert(fact);
    if (!dedup.inserted) {
      errors.push({
        code: 'duplicate_driver_fact',
        message: 'Duplicate governed driver fact at the same driver, dimensions, period, and as-of.',
        driverFactDedupKey: dedup.key,
        evidenceRefId: fact.evidenceRefId,
      });
    }
    driverFactKeys.add(dedup.key);
    errors.push(
      ...validateGovernedDriverFact(
        fact,
        dedup.key,
        input,
        financeFactKeys,
        financeFactByKey,
        financeBridgeByKey,
        factByMatchKey,
      ),
    );
  }

  for (const bridge of input.financeBridges) {
    if (!driverFactKeys.has(bridge.driverFactDedupKey)) {
      errors.push({
        code: 'orphan_finance_bridge',
        message: 'Finance bridge references a driver fact that is not present in the fixture.',
        driverFactDedupKey: bridge.driverFactDedupKey,
        financeFactDedupKey: bridge.financeFactDedupKey,
      });
    }
    if (!financeFactKeys.has(bridge.financeFactDedupKey)) {
      errors.push({
        code: 'missing_finance_fact_reference',
        message: 'Finance bridge references a finance fact that is not present in the fixture.',
        driverFactDedupKey: bridge.driverFactDedupKey,
        financeFactDedupKey: bridge.financeFactDedupKey,
      });
    }
    if (!bridge.asOfAlignment) {
      errors.push({
        code: 'missing_as_of_alignment',
        message:
          'Finance bridge must state the as-of alignment rule before operational and finance facts can be joined.',
        driverFactDedupKey: bridge.driverFactDedupKey,
        financeFactDedupKey: bridge.financeFactDedupKey,
      });
    }
    if (!isValidTimestamp(bridge.asOfAlignment?.operationalAsOf)) {
      errors.push({
        code: 'invalid_as_of_alignment',
        message: 'Finance bridge operationalAsOf must be a valid timestamp.',
        driverFactDedupKey: bridge.driverFactDedupKey,
        financeFactDedupKey: bridge.financeFactDedupKey,
      });
    }
    if (!isValidTimestamp(bridge.asOfAlignment?.financeAsOf)) {
      errors.push({
        code: 'invalid_as_of_alignment',
        message: 'Finance bridge financeAsOf must be a valid timestamp.',
        driverFactDedupKey: bridge.driverFactDedupKey,
        financeFactDedupKey: bridge.financeFactDedupKey,
      });
    }
  }
  const promotedFacts =
    errors.length === 0 ? buildPromotedFactCandidates(input) : [];
  const reconciliationProbes =
    errors.length === 0
      ? buildOperationalDriverReconciliationProbes(input, financeFactByKey)
      : [];

  return {
    ok: errors.length === 0,
    sourceFixtureId: input.sourceFixtureId ?? null,
    promotionStage: input.promotionStage ?? null,
    facts: input.facts,
    financeBridges: input.financeBridges,
    promotedFacts,
    reconciliationProbes,
    errors,
  };
}

function validateGovernedDriverFact(
  fact: GovernedDriverFact,
  driverFactDedupKey: string,
  input: OperationalDriverFixtureLoadInput,
  financeFactKeys: ReadonlySet<string>,
  financeFactByKey: ReadonlyMap<string, OperationalDriverFinanceFact>,
  financeBridgeByKey: ReadonlyMap<string, GovernedDriverFinanceBridge>,
  factByMatchKey: ReadonlyMap<string, GovernedDriverFact>,
): OperationalDriverFixtureLoadError[] {
  const errors: OperationalDriverFixtureLoadError[] = [];
  const requiredStrings: readonly (keyof GovernedDriverFact)[] = [
    'tenantId',
    'driverKey',
    'modeKey',
    'periodType',
    'periodKey',
    'valueNumeric',
    'unit',
    'sourceClass',
    'sourceSystemRef',
    'evidenceRefId',
    'artifactId',
    'ingestJobId',
    'asOf',
  ];

  for (const field of requiredStrings) {
    if (typeof fact[field] !== 'string' || String(fact[field]).trim().length === 0) {
      errors.push({
        code: 'invalid_driver_fact',
        message: `Governed driver fact field ${String(field)} is required.`,
        driverFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }
  }

  if (!isFiniteDecimalString(fact.valueNumeric)) {
    errors.push({
      code: 'invalid_numeric_value',
      message: 'Governed driver fact valueNumeric must be a finite decimal string.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (!isValidTimestamp(fact.asOf)) {
    errors.push({
      code: 'invalid_as_of_alignment',
      message: 'Governed driver fact asOf must be a valid timestamp.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (fact.approvedAt && !isValidTimestamp(fact.approvedAt)) {
    errors.push({
      code: 'invalid_as_of_alignment',
      message: 'Governed driver fact approvedAt must be a valid timestamp when present.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (!isValidPeriodKey(fact.periodType, fact.periodKey)) {
    errors.push({
      code: 'invalid_operational_driver_grain',
      message: 'Governed driver fact period key is invalid for its declared period type.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (input.promotionStage === OPERATIONAL_DRIVER_A3_PROMOTION_STAGE) {
    errors.push(...validateA3FixtureScope(fact, driverFactDedupKey));
  }

  if (
    CLIENT_FEED_ONLY_DRIVER_KEYS.has(fact.driverKey) &&
    fact.disclosureBasis === 'public_filing_reported'
  ) {
    errors.push({
      code: 'driver_requires_client_operational_feed',
      message:
        'This operational driver cannot be claimed as public-filing reported; it requires a client operational feed or must remain a named gap.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  for (const binding of fact.financeBindings) {
    const bridge = financeBridgeByKey.get(
      bridgeKey(driverFactDedupKey, binding.financeFactDedupKey),
    );

    if (!financeFactKeys.has(binding.financeFactDedupKey)) {
      errors.push({
        code: 'missing_finance_fact_reference',
        message: 'Driver fact references a finance fact that is not present in the fixture.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }

    if (!bridge) {
      errors.push({
        code: 'missing_reciprocal_finance_bridge',
        message:
          'Driver fact finance binding must have a reciprocal bridge row before it can support revenue, cost, or productivity decomposition.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }

    if (bridge && !reciprocalBridgeMatchesBinding(binding, bridge)) {
      errors.push({
        code: 'mismatched_reciprocal_finance_bridge',
        message:
          'Driver fact finance binding and reciprocal bridge must match finance metric, relationship, formula, and reconciliation rule.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }

    if (!binding.asOfAlignment || !bridge?.asOfAlignment) {
      errors.push({
        code: 'missing_as_of_alignment',
        message:
          'Driver fact finance binding and reciprocal bridge must state as-of alignment semantics.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    } else {
      errors.push(
        ...validateAsOfAlignment({
          fact,
          binding,
          bridge,
          driverFactDedupKey,
          financeFact: financeFactByKey.get(binding.financeFactDedupKey),
        }),
      );
    }

    if (binding.rateDriverKey) {
      const rateFact = factByMatchKey.get(driverFactMatchKey(fact, binding.rateDriverKey));
      if (!rateFact) {
        errors.push({
          code: 'missing_rate_driver_fact',
          message:
            'Driver-to-finance binding requires the named rate driver fact at the same operating grain.',
          driverFactDedupKey,
          financeFactDedupKey: binding.financeFactDedupKey,
          evidenceRefId: fact.evidenceRefId,
        });
      }
    }
  }

  return errors;
}

function buildA3AsOfAlignment(): GovernedDriverFinanceAsOfAlignment {
  return Object.freeze({
    rule: 'monthly_operational_rollup_to_annual_finance',
    operationalPeriodType: 'month',
    financePeriodType: 'fiscal_year',
    operationalAsOf: A3_FIXTURE_OPERATIONAL_AS_OF,
    financeAsOf: A3_FIXTURE_FINANCE_AS_OF,
  });
}

function buildA3FinanceFact(input: {
  financeMetricKey: string;
  valueNumeric: string;
  evidenceRefId: string;
}): OperationalDriverFinanceFact {
  const financeFactDedupKey = [
    A3_FIXTURE_TENANT_ID,
    input.financeMetricKey,
    'nast',
    GOVERNED_DRIVER_FACT_ALL_MEMBER_KEY,
    GOVERNED_DRIVER_FACT_ALL_MEMBER_KEY,
    'lane:dal-atl',
    GOVERNED_DRIVER_FACT_ALL_MEMBER_KEY,
    'fiscal_year',
    '2025',
    A3_FIXTURE_FINANCE_AS_OF,
  ].join('|');

  return Object.freeze({
    financeFactDedupKey,
    tenantId: A3_FIXTURE_TENANT_ID,
    financeMetricKey: input.financeMetricKey,
    periodType: 'fiscal_year',
    periodKey: '2025',
    valueNumeric: input.valueNumeric,
    unit: 'usd',
    sourceClass: 'loaded_fixture',
    evidenceRefId: input.evidenceRefId,
    artifactId: A3_FIXTURE_ARTIFACT_ID,
    asOf: A3_FIXTURE_FINANCE_AS_OF,
  });
}

function buildA3DriverFact(input: {
  driverKey: string;
  driverFamily: OperationalDriverFamily;
  modeKey: OperationalDriverMode;
  valueNumeric: string;
  unit: string;
  evidenceRefId: string;
  financeBindings: readonly GovernedDriverFinanceBinding[];
}): GovernedDriverFact {
  return Object.freeze({
    schemaVersion: OPERATIONAL_DRIVER_SCHEMA_VERSION,
    tenantId: A3_FIXTURE_TENANT_ID,
    driverKey: input.driverKey,
    driverFamily: input.driverFamily,
    modeKey: input.modeKey,
    businessLineKey: 'nast',
    laneKey: 'lane:dal-atl',
    customerKey: null,
    siteKey: null,
    periodType: 'month',
    periodKey: A3_FIXTURE_PERIOD_KEY,
    valueNumeric: input.valueNumeric,
    unit: input.unit,
    sourceClass: 'loaded_fixture',
    sourceSystemRef: OPERATIONAL_DRIVER_A3_FIXTURE_SOURCE_SYSTEM_REF,
    evidenceRefId: input.evidenceRefId,
    artifactId: A3_FIXTURE_ARTIFACT_ID,
    ingestJobId: A3_FIXTURE_INGEST_JOB_ID,
    asOf: A3_FIXTURE_OPERATIONAL_AS_OF,
    approvedAt: '2026-01-06T00:00:00.000Z',
    disclosureBasis: 'client_operational_feed_required',
    grain: {
      timeGrain: 'monthly_operational' as const,
      dimensions: Object.freeze(['mode', 'lane', 'business_line']),
    },
    financeBindings: input.financeBindings,
  });
}

function validateA3FixtureScope(
  fact: GovernedDriverFact,
  driverFactDedupKey: string,
): OperationalDriverFixtureLoadError[] {
  const errors: OperationalDriverFixtureLoadError[] = [];

  if (!A3_FIXTURE_MODE_KEYS.has(fact.modeKey) || !A3_FIXTURE_DRIVER_KEYS.has(fact.driverKey)) {
    errors.push({
      code: 'unsupported_driver_family_fixture',
      message:
        'A3 operational-driver fixture slice 1 only admits truckload/LTL load, shipment, revenue-per-load, and cost-per-load facts.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (fact.periodType !== 'month' || fact.grain.timeGrain !== 'monthly_operational') {
    errors.push({
      code: 'invalid_operational_driver_grain',
      message:
        'A3 operational-driver fixture slice 1 admits only monthly operational driver facts.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  const declaredDimensions = new Set(fact.grain.dimensions);
  for (const requiredDimension of ['mode', 'lane', 'business_line'] as const) {
    if (!declaredDimensions.has(requiredDimension)) {
      errors.push({
        code: 'invalid_operational_driver_grain',
        message:
          'A3 operational-driver fixture slice 1 requires mode, lane, and business_line grain dimensions.',
        driverFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
      break;
    }
  }

  if (
    (fact.laneKey && !declaredDimensions.has('lane')) ||
    (fact.businessLineKey && !declaredDimensions.has('business_line')) ||
    (fact.customerKey && !declaredDimensions.has('customer')) ||
    (fact.siteKey && !declaredDimensions.has('site'))
  ) {
    errors.push({
      code: 'invalid_operational_driver_grain',
      message:
        'Declared grain dimensions must include every populated operational dimension key.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (
    fact.sourceClass !== 'loaded_fixture' ||
    fact.disclosureBasis !== 'client_operational_feed_required' ||
    !fact.evidenceRefId.startsWith('fixture:evidence:operational-driver-a3:')
  ) {
    errors.push({
      code: 'invalid_driver_fact',
      message:
        'A3 operational-driver facts must be fixture-labeled and client-feed-required.',
      driverFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  return errors;
}

function validateAsOfAlignment(input: {
  fact: GovernedDriverFact;
  binding: GovernedDriverFinanceBinding;
  bridge: GovernedDriverFinanceBridge;
  driverFactDedupKey: string;
  financeFact?: OperationalDriverFinanceFact;
}): OperationalDriverFixtureLoadError[] {
  const errors: OperationalDriverFixtureLoadError[] = [];
  const { fact, binding, bridge, driverFactDedupKey, financeFact } = input;
  const alignment = binding.asOfAlignment;

  if (
    !isValidTimestamp(fact.asOf) ||
    !isValidTimestamp(alignment.operationalAsOf) ||
    !isValidTimestamp(alignment.financeAsOf) ||
    !isValidTimestamp(bridge.asOfAlignment.operationalAsOf) ||
    !isValidTimestamp(bridge.asOfAlignment.financeAsOf) ||
    (financeFact ? !isValidTimestamp(financeFact.asOf) : false)
  ) {
    errors.push({
      code: 'invalid_as_of_alignment',
      message:
        'Driver-to-finance alignment requires valid operational and finance as-of timestamps.',
      driverFactDedupKey,
      financeFactDedupKey: binding.financeFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
    return errors;
  }

  if (
    bridge.asOfAlignment.rule !== alignment.rule ||
    bridge.asOfAlignment.operationalAsOf !== alignment.operationalAsOf ||
    bridge.asOfAlignment.financeAsOf !== alignment.financeAsOf
  ) {
    errors.push({
      code: 'cross_grain_join_refused',
      message:
        'Driver fact binding and reciprocal bridge must agree on the as-of alignment rule and cut timestamps.',
      driverFactDedupKey,
      financeFactDedupKey: binding.financeFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (
    alignment.operationalPeriodType !== fact.periodType ||
    alignment.operationalAsOf !== fact.asOf
  ) {
    errors.push({
      code: 'cross_grain_join_refused',
      message:
        'Operational as-of alignment must match the driver fact period type and source cut timestamp.',
      driverFactDedupKey,
      financeFactDedupKey: binding.financeFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (financeFact) {
    if (
      alignment.financePeriodType !== financeFact.periodType ||
      alignment.financeAsOf !== financeFact.asOf
    ) {
      errors.push({
        code: 'cross_grain_join_refused',
        message:
          'Finance as-of alignment must match the referenced finance fact period type and source cut timestamp.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }

    if (!financePeriodContainsOperationalPeriod(financeFact, fact)) {
      errors.push({
        code: 'cross_grain_join_refused',
        message:
          'Monthly operational driver fact is outside the referenced annual finance period.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }

    if (Date.parse(fact.asOf) > Date.parse(financeFact.asOf)) {
      errors.push({
        code: 'stale_operational_as_of',
        message:
          'Operational source cut is after the finance source cut and cannot explain that finance fact.',
        driverFactDedupKey,
        financeFactDedupKey: binding.financeFactDedupKey,
        evidenceRefId: fact.evidenceRefId,
      });
    }
  }

  if (
    alignment.rule === 'monthly_operational_rollup_to_annual_finance' &&
    (fact.periodType !== 'month' || alignment.financePeriodType !== 'fiscal_year')
  ) {
    errors.push({
      code: 'cross_grain_join_refused',
      message:
        'Monthly-to-annual alignment requires a monthly operational fact and fiscal-year finance fact.',
      driverFactDedupKey,
      financeFactDedupKey: binding.financeFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  if (fact.periodType === 'month' && Date.parse(fact.asOf) < monthEndUtc(fact.periodKey)) {
    errors.push({
      code: 'stale_operational_as_of',
      message:
        'Monthly operational as-of cut must be at or after the close of the operational month.',
      driverFactDedupKey,
      financeFactDedupKey: binding.financeFactDedupKey,
      evidenceRefId: fact.evidenceRefId,
    });
  }

  return errors;
}

function buildPromotedFactCandidates(
  input: OperationalDriverFixtureLoadInput,
): readonly OperationalDriverPromotedFactCandidate[] {
  const sourceFixtureId = input.sourceFixtureId ?? 'unspecified-fixture';
  const promotionStage = input.promotionStage ?? OPERATIONAL_DRIVER_A3_PROMOTION_STAGE;

  return Object.freeze(
    input.facts.map((fact) => {
      const driverFactDedupKey = buildGovernedDriverFactDedupKey(fact);
      return Object.freeze({
        id: `promoted:${driverFactDedupKey}`,
        fact,
        promotion: Object.freeze({
          pattern: promotionStage,
          parent: Object.freeze({
            refType: 'fixture_driver_fact' as const,
            sourceFixtureId,
            driverFactDedupKey,
            evidenceRefId: fact.evidenceRefId,
            artifactId: fact.artifactId,
            ingestJobId: fact.ingestJobId,
            asOf: fact.asOf,
          }),
        }),
      });
    }),
  );
}

function buildOperationalDriverReconciliationProbes(
  input: OperationalDriverFixtureLoadInput,
  financeFactByKey: ReadonlyMap<string, OperationalDriverFinanceFact>,
): readonly OperationalDriverReconciliationProbe[] {
  const factByKey = new Map<string, GovernedDriverFact>(
    input.facts.map((fact) => [buildGovernedDriverFactDedupKey(fact), fact] as const),
  );
  const factByMatch = new Map<string, GovernedDriverFact>(
    input.facts.map((fact) => [driverFactMatchKey(fact, fact.driverKey), fact] as const),
  );
  const probes: OperationalDriverReconciliationProbe[] = [];

  for (const bridge of input.financeBridges) {
    const driverFact = factByKey.get(bridge.driverFactDedupKey);
    const financeFact = financeFactByKey.get(bridge.financeFactDedupKey);
    if (!driverFact || !financeFact) continue;

    const binding = driverFact.financeBindings.find(
      (candidate) => candidate.financeFactDedupKey === bridge.financeFactDedupKey,
    );
    if (!binding?.rateDriverKey) continue;

    const rateFact = factByMatch.get(driverFactMatchKey(driverFact, binding.rateDriverKey));
    if (!rateFact) continue;

    const driverValueNumeric = parseFiniteNumber(driverFact.valueNumeric);
    const rateValueNumeric = parseFiniteNumber(rateFact.valueNumeric);
    const financeValueNumeric = parseFiniteNumber(financeFact.valueNumeric);
    if (
      driverValueNumeric === null ||
      rateValueNumeric === null ||
      financeValueNumeric === null
    ) {
      continue;
    }

    const computedFinanceValueNumeric = roundNumber(driverValueNumeric * rateValueNumeric, 6);
    probes.push(
      Object.freeze({
        severity: 'info' as const,
        code: 'driver_finance_reconciliation_info' as const,
        financeFactDedupKey: bridge.financeFactDedupKey,
        financeMetricKey: financeFact.financeMetricKey,
        formulaId: bridge.formulaId,
        relationship: bridge.relationship,
        driverFactDedupKey: bridge.driverFactDedupKey,
        rateDriverFactDedupKey: buildGovernedDriverFactDedupKey(rateFact),
        driverValueNumeric,
        rateValueNumeric,
        computedFinanceValueNumeric,
        financeValueNumeric,
        residualNumeric: roundNumber(financeValueNumeric - computedFinanceValueNumeric, 6),
        unit: financeFact.unit,
        message:
          'INFO only: fixture-labeled driver x rate arithmetic is reconciled against a fixture finance fact and does not create annual operational truth.',
      }),
    );
  }

  return Object.freeze(probes);
}

function financePeriodContainsOperationalPeriod(
  financeFact: OperationalDriverFinanceFact,
  fact: GovernedDriverFact,
): boolean {
  if (financeFact.periodType === fact.periodType) {
    return financeFact.periodKey === fact.periodKey;
  }

  if (financeFact.periodType === 'fiscal_year' && fact.periodType === 'month') {
    return fact.periodKey.slice(0, 4) === financeFact.periodKey;
  }

  if (financeFact.periodType === 'month' && fact.periodType === 'week') {
    return fact.periodKey.startsWith(financeFact.periodKey);
  }

  return false;
}

function monthEndUtc(periodKey: string): number {
  const [yearRaw, monthRaw] = periodKey.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return Number.NaN;
  }
  return Date.UTC(year, month, 0, 23, 59, 59, 999);
}

function driverFactMatchKey(fact: GovernedDriverFact, driverKey: string): string {
  return [
    fact.tenantId,
    driverKey,
    fact.modeKey,
    dimensionKey(fact.businessLineKey),
    dimensionKey(fact.laneKey),
    dimensionKey(fact.customerKey),
    dimensionKey(fact.siteKey),
    fact.periodType,
    fact.periodKey,
    fact.asOf,
  ].join('|');
}

function reciprocalBridgeMatchesBinding(
  binding: GovernedDriverFinanceBinding,
  bridge: GovernedDriverFinanceBridge,
): boolean {
  return (
    bridge.financeMetricKey === binding.financeMetricKey &&
    bridge.relationship === binding.relationship &&
    bridge.formulaId === binding.formulaId &&
    bridge.reconciliationRule === binding.reconciliationRule
  );
}

function parseFiniteNumber(value: string): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isFiniteDecimalString(value: string): boolean {
  return typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value));
}

function isValidTimestamp(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '' && Number.isFinite(Date.parse(value));
}

function isValidPeriodKey(
  periodType: OperationalDriverPeriodType,
  periodKey: string,
): boolean {
  if (periodType === 'month') return /^\d{4}-(0[1-9]|1[0-2])$/.test(periodKey);
  if (periodType === 'fiscal_year') return /^\d{4}$/.test(periodKey);
  if (periodType === 'quarter') return /^\d{4}-Q[1-4]$/.test(periodKey);
  return /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/.test(periodKey);
}

function roundNumber(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function dimensionKey(value: string | null | undefined): string {
  return value ?? GOVERNED_DRIVER_FACT_ALL_MEMBER_KEY;
}

function bridgeKey(driverFactDedupKey: string, financeFactDedupKey: string): string {
  return `${driverFactDedupKey}=>${financeFactDedupKey}`;
}
